import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import { useCookieConsent } from "./CookieConsentContext";

const openingHours = [
  { day: "Montag", hours: "Geschlossen", closed: true },
  { day: "Dienstag", hours: "Geschlossen", closed: true },
  { day: "Mittwoch", hours: "16:00 – 23:00", closed: false },
  { day: "Donnerstag", hours: "16:00 – 23:00", closed: false },
  { day: "Freitag", hours: "16:00 – 02:00", closed: false, highlight: true },
  { day: "Samstag", hours: "13:00 – 02:00", closed: false, highlight: true },
  { day: "Sonntag", hours: "13:00 – 23:00", closed: false },
];

const today = new Date().toLocaleDateString("de-DE", { weekday: "long" });

const contactItems = [
  {
    icon: MapPin,
    label: "Adresse",
    value: "Hainstraße 4\n92355 Velburg",
    href: "https://maps.google.com/?q=Pirate+Bowling+Center+Hainstraße+4+Velburg",
    testId: "contact-address",
  },
  {
    icon: Phone,
    label: "Telefon",
    value: "09182 938936",
    href: "tel:+499182938936",
    testId: "contact-phone",
  },
  {
    icon: Mail,
    label: "E-Mail",
    value: "info@pirate-bowling.com",
    href: "mailto:info@pirate-bowling.com",
    testId: "contact-email",
  },
  {
    icon: ExternalLink,
    label: "Reservierung",
    value: "Jetzt Bahn buchen",
    href: "https://4bowl.de/pirate-bowling/online/",
    testId: "contact-booking",
  },
];

export default function Location() {
  const { consent, reopenBanner } = useCookieConsent();
  const mapAllowed = consent?.externalMedia;

  return (
    <section
      id="location"
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-20 bg-navy"
      data-testid="location-section"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="skull-divider mb-6">
            <span className="font-space-grotesk text-xs text-gold tracking-[0.3em] uppercase px-3">Standort & Kontakt</span>
          </div>
          <h2 className="font-unbounded text-4xl sm:text-5xl lg:text-6xl font-black text-cream leading-tight">
            Besuche uns in{" "}
            <span className="gold-gradient-text">Velburg</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Left Column */}
          <div className="flex flex-col gap-5">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-2xl border border-gold/22 flex-1 min-h-[280px]"
              data-testid="google-map"
            >
              {mapAllowed ? (
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2572.9!2d11.6808!3d49.2344!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479f5ff7f5e0001f%3A0x1!2sHainstra%C3%9Fe+4%2C+92355+Velburg!5e0!3m2!1sde!2sde!4v1680000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pirate Bowling Center Velburg"
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-navy-deep/50 p-6 text-center">
                  <MapPin className="w-10 h-10 text-gold/40 mb-4" />
                  <p className="font-space-grotesk text-sm text-cream/80 mb-1">Google Maps ist deaktiviert</p>
                  <p className="font-inter text-xs text-cream/50 mb-4">Aktiviere externe Medien, um die Karte zu sehen.</p>
                  <button
                    onClick={reopenBanner}
                    className="btn-outline px-5 py-2 rounded-full text-xs"
                    data-testid="map-consent-btn"
                  >
                    Cookie-Einstellungen öffnen
                  </button>
                </div>
              )}
            </motion.div>

            {/* Contact Cards */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="grid grid-cols-2 gap-3"
            >
              {contactItems.map((item) => (
                <a
                  key={item.testId}
                  href={item.href}
                  target={item.label === "Reservierung" || item.label === "Adresse" ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group glass border border-white/5 hover:border-gold/30 rounded-xl p-4 flex items-start gap-3 transition-all duration-300 hover:bg-gold/8"
                  data-testid={item.testId}
                >
                  <div className="w-8 h-8 rounded-lg bg-gold/10 border border-gold/28 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                    <item.icon className="w-4 h-4 text-gold" />
                  </div>
                  <div>
                    <div className="font-space-grotesk text-xs text-cream/50 uppercase tracking-widest mb-1">{item.label}</div>
                    <div className="font-inter text-xs text-cream/80 leading-relaxed whitespace-pre-line">{item.value}</div>
                  </div>
                </a>
              ))}
            </motion.div>
          </div>

          {/* Right Column – Opening Hours */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass border border-gold/22 rounded-2xl p-8"
            data-testid="opening-hours"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-unbounded text-lg font-bold text-cream">Öffnungszeiten</h3>
                <p className="font-space-grotesk text-xs text-cream/50 mt-0.5 uppercase tracking-widest">Bowling Velburg</p>
              </div>
            </div>

            <div className="space-y-1.5">
              {openingHours.map((item) => {
                const isToday = today === item.day;
                return (
                  <div
                    key={item.day}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      isToday ? "today-row" : ""
                    }`}
                    data-testid={`hours-${item.day.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3">
                      {isToday && <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />}
                      <span className={`font-space-grotesk text-sm ${
                        isToday ? "text-gold font-semibold" : item.closed ? "text-cream/40" : "text-cream/70"
                      }`}>
                        {item.day}
                      </span>
                      {isToday && (
                        <span className="font-space-grotesk text-xs bg-gold text-navy px-2 py-0.5 rounded-full font-bold">
                          Heute
                        </span>
                      )}
                    </div>
                    <span className={`font-space-grotesk text-sm font-semibold ${
                      isToday ? "text-gold" : item.closed ? "text-cream/40" : item.highlight ? "text-gold/80" : "text-cream/80"
                    }`}>
                      {item.hours}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-7 pt-7 border-t border-white/5">
              <p className="font-inter text-xs text-cream/45 text-center leading-relaxed">
                Sonderöffnungszeiten an Feiertagen und in den Schulferien möglich.
                <br />
                Aktuelle Infos auf unseren Social-Media-Kanälen.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
